import React, { useState, useEffect, useCallback } from 'react';
import { masterSupabase } from '@/lib/supabase-master.js';
import { useToast } from "../notifications/ToastProvider";
import { 
  Building, 
  Plus, 
  Edit, 
  Trash2, 
  Home, 
  Users, 
  Euro, 
  Square, 
  Bed, 
  Bath, 
  Car, 
  Wifi, 
  Zap, 
  TreePine, 
  Shield, 
  TrendingUp, 
  BarChart3, 
  Target, 
  Award,
  ArrowLeft,
  Save,
  X,
  Palette,
  Thermometer
} from 'lucide-react';

const UnitsManagerModal = ({ isOpen, onClose, property, onUpdate }) => {
  const { showSuccess, showError } = useToast();
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentView, setCurrentView] = useState('overview'); // 'overview' or 'edit'
  const [editingUnit, setEditingUnit] = useState(null);
  const [formData, setFormData] = useState({
    unit_identifier: '',
    unit_type: 'wohnung',
    floor: '',
    size_sqm: '',
    rooms: '',
    bedrooms: '',
    bathrooms: '',
    monthly_rent: '',
    cold_rent: '',
    utilities_cost: '',
    rent_type: 'Kaltmiete',
    status: 'available',
    heating_type: '',
    floor_type: '',
    available_from: '',
    last_renovation: '',
    description: '',
    // Features
    balcony: false,
    parking: false,
    elevator: false,
    furnished: false,
    pets_allowed: false,
    smoking_allowed: false,
    internet_included: false,
    utilities_included: false
  });

  // Debug logging
  useEffect(() => {
    console.log('🔍 UnitsManagerModal Debug:', {
      isOpen,
      property: property ? {
        id: property.id,
        address: property.address
      } : null
    });
  }, [isOpen, property]);

  // Fetch units for the property
  const fetchUnits = useCallback(async () => {
    if (!property?.id) {
      console.log('❌ No property ID provided:', property);
      return;
    }
    
    console.log('🔄 Fetching units for property ID:', property.id);
    setLoading(true);
    setError(null);
    
    try {
      // Try different possible column names for property reference
      let query = masterSupabase.from('units').select('*');
      
      // First try with property_id
      let { data, error: fetchError } = await query.eq('property_id', property.id).order('unit_identifier');
      
      // If no results, try with property_name
      if (!data || data.length === 0) {
        console.log('🔄 No units found with property_id, trying property_name...');
        const { data: data2, error: error2 } = await masterSupabase
          .from('units')
          .select('*')
          .eq('property_name', property.property_name)
          .order('unit_identifier');
        
        data = data2;
        fetchError = error2;
      }
      
      // If still no results, try with address
      if (!data || data.length === 0) {
        console.log('🔄 No units found with property_name, trying address...');
        const { data: data3, error: error3 } = await masterSupabase
          .from('units')
          .select('*')
          .eq('address', property.address)
          .order('unit_identifier');
        
        data = data3;
        fetchError = error3;
      }

      if (fetchError) {
        console.error('❌ Database error:', fetchError);
        throw fetchError;
      }
      
      console.log('✅ Units loaded:', data?.length || 0, 'units');
      console.log('📊 Sample unit data:', data?.[0]);
      
      setUnits(data || []);
    } catch (err) {
      console.error('❌ Error fetching units:', err);
      setError(`Fehler beim Laden der Einheiten: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [property?.id, property?.property_name, property?.address]);

  useEffect(() => {
    if (isOpen && property?.id) {
      fetchUnits();
    }
  }, [isOpen, property?.id, fetchUnits]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let result;
      const submitData = {
        ...formData,
        property_id: property.id,
        // Convert numeric fields
        floor: formData.floor ? parseInt(formData.floor) : null,
        size_sqm: formData.size_sqm ? parseFloat(formData.size_sqm) : null,
        rooms: formData.rooms ? parseFloat(formData.rooms) : null,
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : null,
        monthly_rent: formData.monthly_rent ? parseFloat(formData.monthly_rent) : null,
        cold_rent: formData.cold_rent ? parseFloat(formData.cold_rent) : null,
        utilities_cost: formData.utilities_cost ? parseFloat(formData.utilities_cost) : null,
        // Convert date fields - send null if empty
        available_from: formData.available_from || null,
        last_renovation: formData.last_renovation || null
      };

      if (editingUnit) {
        // Update existing unit
        result = await masterSupabase
          .from('units')
          .update({
            ...submitData,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingUnit.id);
      } else {
        // Create new unit
        const user = await masterSupabase.auth.getUser();
        result = await masterSupabase
          .from('units')
          .insert([{
            ...submitData,
            created_by: user.data.user?.id,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }]);
      }

      if (result.error) throw result.error;

      // Refresh units list
      await fetchUnits();
      
      // Reset form and go back to overview
      resetForm();
      setCurrentView('overview');
      
      // Notify parent component
      if (onUpdate) onUpdate();
      
      // Show success message and close modal
      showSuccess(editingUnit ? "Einheit erfolgreich aktualisiert" : "Einheit erfolgreich erstellt");
      onClose();
    } catch (err) {
      console.error('Error saving unit:', err);
      setError('Fehler beim Speichern der Einheit');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (unitId) => {
    if (!confirm('Sind Sie sicher, dass Sie diese Einheit löschen möchten?')) return;

    setLoading(true);
    try {
      const { error: deleteError } = await masterSupabase
        .from('units')
        .delete()
        .eq('id', unitId);

      if (deleteError) throw deleteError;

      await fetchUnits();
      if (onUpdate) onUpdate();
      
      // Show success message and close modal
      showSuccess(editingUnit ? "Einheit erfolgreich aktualisiert" : "Einheit erfolgreich erstellt");
      onClose();
    } catch (err) {
      console.error('Error deleting unit:', err);
      setError('Fehler beim Löschen der Einheit');
    } finally {
      setLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (unit) => {
    setEditingUnit(unit);
    setFormData({
      unit_identifier: unit.unit_identifier || '',
      unit_type: unit.unit_type || 'wohnung',
      floor: unit.floor || '',
      size_sqm: unit.size_sqm || '',
      rooms: unit.rooms || '',
      bedrooms: unit.bedrooms || '',
      bathrooms: unit.bathrooms || '',
      monthly_rent: unit.monthly_rent || '',
      cold_rent: unit.cold_rent || '',
      utilities_cost: unit.utilities_cost || '',
      rent_type: unit.rent_type || 'Kaltmiete',
      status: unit.status || 'available',
      heating_type: unit.heating_type || '',
      floor_type: unit.floor_type || '',
      available_from: unit.available_from || '',
      last_renovation: unit.last_renovation || '',
      description: unit.description || '',
      balcony: unit.balcony || false,
      parking: unit.parking || false,
      elevator: unit.elevator || false,
      furnished: unit.furnished || false,
      pets_allowed: unit.pets_allowed || false,
      smoking_allowed: unit.smoking_allowed || false,
      internet_included: unit.internet_included || false,
      utilities_included: unit.utilities_included || false
    });
    setCurrentView('edit');
  };

  // Reset form
  const resetForm = () => {
    setEditingUnit(null);
    setFormData({
      unit_identifier: '',
      unit_type: 'wohnung',
      floor: '',
      size_sqm: '',
      rooms: '',
      bedrooms: '',
      bathrooms: '',
      monthly_rent: '',
      cold_rent: '',
      utilities_cost: '',
      rent_type: 'Kaltmiete',
      status: 'available',
      heating_type: '',
      floor_type: '',
      available_from: '',
      last_renovation: '',
      description: '',
      balcony: false,
      parking: false,
      elevator: false,
      furnished: false,
      pets_allowed: false,
      smoking_allowed: false,
      internet_included: false,
      utilities_included: false
    });
  };

  // Calculate insights
  const calculateInsights = () => {
    if (units.length === 0) return null;

    const totalUnits = units.length;
    const rentedUnits = units.filter(u => u.status === 'occupied').length;
    const availableUnits = units.filter(u => u.status === 'available').length;
    const avgMonthlyRent = units.reduce((sum, u) => sum + (parseFloat(u.monthly_rent) || 0), 0) / totalUnits;
    const avgColdRent = units.reduce((sum, u) => sum + (parseFloat(u.cold_rent) || 0), 0) / totalUnits;
    const avgArea = units.reduce((sum, u) => sum + (parseFloat(u.size_sqm) || 0), 0) / totalUnits;
    const avgColdRentPerSqm = avgArea > 0 ? avgColdRent / avgArea : 0;

    return {
      totalUnits,
      rentedUnits,
      availableUnits,
      occupancyRate: totalUnits > 0 ? (rentedUnits / totalUnits) * 100 : 0,
      avgMonthlyRent,
      avgColdRent,
      avgArea,
      avgColdRentPerSqm
    };
  };

  const insights = calculateInsights();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-7xl h-[90vh] flex flex-col overflow-hidden">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {currentView === 'edit' && (
              <button
                onClick={() => {
                  setCurrentView('overview');
                  resetForm();
                }}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <Building className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">
                {currentView === 'overview' ? 'Einheiten verwalten' : 
                 editingUnit ? 'Einheit bearbeiten' : 'Neue Einheit'}
              </h2>
              <p className="text-blue-100 text-sm">
                {currentView === 'overview' 
                  ? `${property?.property_name || property?.address || 'Immobilie'}`
                  : `Bearbeiten Sie die Einheitsinformationen`
                }
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          
          {/* Main Content */}
          <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
            {currentView === 'overview' ? (
              // Overview: Unit Cards
              <div className="p-6">
                {/* Header with Add Button */}
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                    Einheiten ({units.length})
                  </h3>
                  <button
                    onClick={() => {
                      resetForm();
                      setCurrentView('edit');
                    }}
                    className="bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 flex items-center space-x-2 shadow-lg"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Neue Einheit</span>
                  </button>
                </div>

                {/* Debug Info */}
                {error && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                    <p className="text-red-800 dark:text-red-200">{error}</p>
                    <p className="text-red-600 dark:text-red-300 text-sm mt-2">
                      Property ID: {property?.id}, Name: {property?.property_name}
                    </p>
                  </div>
                )}

                {/* Loading State */}
                {loading && (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600 dark:text-gray-300">Lade Einheiten...</span>
                  </div>
                )}

                {/* Units Grid */}
                {!loading && !error && units.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {units.map((unit) => (
                      <div key={unit.id} className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all duration-200">
                        {/* Unit Header */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-2">
                            <Home className="w-5 h-5 text-blue-600" />
                            <h4 className="font-semibold text-gray-800 dark:text-white">
                              Einheit {unit.unit_identifier}
                            </h4>
                          </div>
                          <div className="flex space-x-1">
                            <button
                              onClick={() => handleEdit(unit)}
                              className="p-2 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(unit.id)}
                              className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Status Badge */}
                        <div className="mb-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                            unit.status === 'occupied' 
                              ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200' 
                              : unit.status === 'available'
                              ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200'
                              : 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200'
                          }`}>
                            {unit.status === 'occupied' ? 'Vermietet' : 
                             unit.status === 'available' ? 'Verfügbar' : 
                             unit.status === 'maintenance' ? 'Wartung' : unit.status}
                          </span>
                        </div>

                        {/* Unit Details */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="flex items-center text-gray-600 dark:text-gray-400">
                              <Square className="w-4 h-4 mr-2" />
                              Zimmer:
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">{unit.rooms || '-'}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="flex items-center text-gray-600 dark:text-gray-400">
                              <Building className="w-4 h-4 mr-2" />
                              Etage:
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">{unit.floor || '-'}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="flex items-center text-gray-600 dark:text-gray-400">
                              <Square className="w-4 h-4 mr-2" />
                              Fläche:
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">{unit.size_sqm ? `${unit.size_sqm} m²` : '-'}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="flex items-center text-gray-600 dark:text-gray-400">
                              <Euro className="w-4 h-4 mr-2" />
                              Kaltmiete:
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">{unit.cold_rent ? `€${unit.cold_rent}` : '-'}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="flex items-center text-gray-600 dark:text-gray-400">
                              <Euro className="w-4 h-4 mr-2" />
                              Gesamtmiete:
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">{unit.monthly_rent ? `€${unit.monthly_rent}` : '-'}</span>
                          </div>
                        </div>

                        {/* Features */}
                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                          <div className="flex flex-wrap gap-2">
                            {unit.balcony && (
                              <span className="inline-flex items-center px-2 py-1 bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200 text-xs rounded-full">
                                <TreePine className="w-3 h-3 mr-1" />
                                Balkon
                              </span>
                            )}
                            {unit.parking && (
                              <span className="inline-flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 text-xs rounded-full">
                                <Car className="w-3 h-3 mr-1" />
                                Parkplatz
                              </span>
                            )}
                            {unit.furnished && (
                              <span className="inline-flex items-center px-2 py-1 bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-200 text-xs rounded-full">
                                <Home className="w-3 h-3 mr-1" />
                                Möbliert
                              </span>
                            )}
                            {unit.floor_type && (
                              <span className="inline-flex items-center px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs rounded-full">
                                <Palette className="w-3 h-3 mr-1" />
                                {unit.floor_type}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Empty State */}
                {!loading && !error && units.length === 0 && (
                  <div className="text-center py-12">
                    <Building className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Keine Einheiten</h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-6">Erstellen Sie die erste Einheit für diese Immobilie.</p>
                    <button
                      onClick={() => {
                        resetForm();
                        setCurrentView('edit');
                      }}
                      className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 flex items-center space-x-2 mx-auto shadow-lg"
                    >
                      <Plus className="w-5 h-5" />
                      <span>Erste Einheit erstellen</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              // Edit Form
              <div className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Information */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                      <Home className="w-5 h-5 mr-2 text-blue-600" />
                      Grundinformationen
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Einheitsnummer *
                        </label>
                        <input
                          type="text"
                          value={formData.unit_identifier}
                          onChange={(e) => setFormData({...formData, unit_identifier: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="z.B. EH 01 - EG links"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Typ
                        </label>
                        <select
                          value={formData.unit_type}
                          onChange={(e) => setFormData({...formData, unit_type: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="wohnung">Wohnung</option>
                          <option value="gewerbe">Gewerbe</option>
                          <option value="buero">Büro</option>
                          <option value="lager">Lager</option>
                          <option value="stellplatz">Stellplatz</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Status
                        </label>
                        <select
                          value={formData.status}
                          onChange={(e) => setFormData({...formData, status: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="available">Verfügbar</option>
                          <option value="occupied">Vermietet</option>
                          <option value="maintenance">Wartung</option>
                          <option value="reserved">Reserviert</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Etage
                        </label>
                        <input
                          type="number"
                          value={formData.floor}
                          onChange={(e) => setFormData({...formData, floor: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="0 = EG"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Fläche (m²)
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          value={formData.size_sqm}
                          onChange={(e) => setFormData({...formData, size_sqm: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="z.B. 65.5"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Zimmer
                        </label>
                        <input
                          type="number"
                          step="0.5"
                          value={formData.rooms}
                          onChange={(e) => setFormData({...formData, rooms: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="4"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Schlafzimmer
                        </label>
                        <input
                          type="number"
                          value={formData.bedrooms}
                          onChange={(e) => setFormData({...formData, bedrooms: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="z.B. 1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Rent Information */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                      <Euro className="w-5 h-5 mr-2 text-green-600" />
                      Mietinformationen
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Kaltmiete (€)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.cold_rent}
                          onChange={(e) => {
                            const coldRent = parseFloat(e.target.value) || 0;
                            const utilities = parseFloat(formData.utilities_cost) || 0;
                            setFormData({
                              ...formData, 
                              cold_rent: e.target.value,
                              monthly_rent: (coldRent + utilities).toString()
                            });
                          }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="z.B. 750.00"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Nebenkosten (€)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.utilities_cost}
                          onChange={(e) => {
                            const coldRent = parseFloat(formData.cold_rent) || 0;
                            const utilities = parseFloat(e.target.value) || 0;
                            setFormData({
                              ...formData, 
                              utilities_cost: e.target.value,
                              monthly_rent: (coldRent + utilities).toString()
                            });
                          }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="z.B. 100.00"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Gesamtmiete (€)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.monthly_rent}
                          onChange={(e) => setFormData({...formData, monthly_rent: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-600"
                          placeholder="Automatisch berechnet"
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                      <Award className="w-5 h-5 mr-2 text-purple-600" />
                      Ausstattung & Merkmale
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[
                        { key: 'balcony', label: 'Balkon', icon: TreePine },
                        { key: 'parking', label: 'Parkplatz', icon: Car },
                        { key: 'elevator', label: 'Aufzug', icon: Building },
                        { key: 'furnished', label: 'Möbliert', icon: Home },
                        { key: 'pets_allowed', label: 'Haustiere erlaubt', icon: Shield },
                        { key: 'smoking_allowed', label: 'Rauchen erlaubt', icon: Zap },
                        { key: 'internet_included', label: 'Internet inklusive', icon: Wifi },
                        { key: 'utilities_included', label: 'Nebenkosten inklusive', icon: Euro }
                      ].map(({ key, label, icon: Icon }) => (
                        <label key={key} className="flex items-center space-x-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData[key]}
                            onChange={(e) => setFormData({...formData, [key]: e.target.checked})}
                            className="w-4 h-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500"
                          />
                          <Icon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                      Zusätzliche Informationen
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          <Thermometer className="w-4 h-4 inline mr-1" />
                          Heizungstyp
                        </label>
                        <input
                          type="text"
                          value={formData.heating_type}
                          onChange={(e) => setFormData({...formData, heating_type: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="z.B. Zentralheizung, Fußbodenheizung"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          <Palette className="w-4 h-4 inline mr-1" />
                          Bodenbelag
                        </label>
                        <select
                          value={formData.floor_type}
                          onChange={(e) => setFormData({...formData, floor_type: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="">Bitte wählen...</option>
                          <optgroup label="Hartböden">
                            <option value="parkett">Parkett</option>
                            <option value="laminat">Laminat</option>
                            <option value="vinyl">Vinyl/LVT</option>
                            <option value="fliesen">Fliesen</option>
                            <option value="naturstein">Naturstein</option>
                          </optgroup>
                          <optgroup label="Weichböden">
                            <option value="teppich">Teppichboden</option>
                            <option value="pvc">PVC</option>
                            <option value="linoleum">Linoleum</option>
                          </optgroup>
                          <optgroup label="Sonstige">
                            <option value="estrich">Estrich</option>
                            <option value="gemischt">Gemischt</option>
                          </optgroup>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Verfügbar ab
                        </label>
                        <input
                          type="date"
                          value={formData.available_from}
                          onChange={(e) => setFormData({...formData, available_from: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Letzte Renovierung
                        </label>
                        <input
                          type="date"
                          value={formData.last_renovation}
                          onChange={(e) => setFormData({...formData, last_renovation: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Beschreibung
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Zusätzliche Informationen zur Einheit..."
                      />
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentView('overview');
                        resetForm();
                      }}
                      className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Abbrechen
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-2 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      <span>{editingUnit ? 'Änderungen speichern' : 'Einheit erstellen'}</span>
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* Live Insights Sidebar */}
          {insights && (
            <div className="w-80 bg-gray-50 dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 overflow-y-auto">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                  <BarChart3 className="w-5 h-5 mr-2 text-blue-600" />
                  Live Insights
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  Intelligente Analysen & Empfehlungen
                </p>

                {/* Overview Stats */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 mb-6 border border-gray-200 dark:border-gray-700">
                  <h4 className="font-semibold text-gray-800 dark:text-white mb-3 flex items-center">
                    <Target className="w-4 h-4 mr-2 text-green-600" />
                    Objektvergleich
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Einheiten gesamt:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{insights.totalUnits}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Ø Kaltmiete:</span>
                      <span className="font-medium text-gray-900 dark:text-white">€{insights.avgColdRent.toFixed(0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Ø Gesamtmiete:</span>
                      <span className="font-medium text-gray-900 dark:text-white">€{insights.avgMonthlyRent.toFixed(0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Ø Kaltmiete/m²:</span>
                      <span className="font-medium text-gray-900 dark:text-white">€{insights.avgColdRentPerSqm.toFixed(2)}</span>
                    </div>
                    <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                      <div className="flex justify-between">
                        <span className="text-green-600 dark:text-green-400 font-medium">
                          {insights.occupancyRate > 80 ? '↗' : insights.occupancyRate > 60 ? '→' : '↘'} 
                          {insights.occupancyRate.toFixed(1)}% Auslastung
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Unit Comparison */}
                {currentView === 'edit' && editingUnit && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-4 mb-6 border border-gray-200 dark:border-gray-700">
                    <h4 className="font-semibold text-gray-800 dark:text-white mb-3 flex items-center">
                      <Home className="w-4 h-4 mr-2 text-blue-600" />
                      Einheit {editingUnit.unit_identifier}
                    </h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Größe:</span>
                        <span className="font-medium text-gray-900 dark:text-white">{editingUnit.size_sqm} m²</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Kaltmiete/m²:</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          €{editingUnit.size_sqm ? (parseFloat(editingUnit.cold_rent || 0) / parseFloat(editingUnit.size_sqm)).toFixed(2) : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Nebenkosten:</span>
                        <span className="font-medium text-gray-900 dark:text-white">€{editingUnit.utilities_cost || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Gesamtmiete:</span>
                        <span className="font-medium text-gray-900 dark:text-white">€{editingUnit.monthly_rent || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Ø Kaltmiete/m²:</span>
                        <span className="font-medium text-gray-900 dark:text-white">€{insights.avgColdRentPerSqm.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Market Analysis */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 mb-6 border border-gray-200 dark:border-gray-700">
                  <h4 className="font-semibold text-gray-800 dark:text-white mb-3 flex items-center">
                    <TrendingUp className="w-4 h-4 mr-2 text-purple-600" />
                    Vertragsanalyse
                  </h4>
                  <div className="space-y-3">
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                      <div className="text-sm font-medium text-blue-800 dark:text-blue-200">Empfehlung</div>
                      <div className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                        Kaltmiete liegt {insights.avgColdRentPerSqm > 12 ? 'über' : 'unter'} dem Marktdurchschnitt
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Auslastung:</span>
                        <span className="font-medium text-gray-900 dark:text-white">{insights.occupancyRate.toFixed(1)}%</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Verfügbar:</span>
                        <span className="font-medium text-gray-900 dark:text-white">{insights.availableUnits} Einheiten</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Vermietet:</span>
                        <span className="font-medium text-gray-900 dark:text-white">{insights.rentedUnits} Einheiten</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Performance Indicators */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <h4 className="font-semibold text-gray-800 dark:text-white mb-3">Performance</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Vermietungsquote</span>
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mr-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full" 
                            style={{width: `${insights.occupancyRate}%`}}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{insights.occupancyRate.toFixed(0)}%</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Marktaktivität</span>
                      <span className="text-sm font-medium text-green-600 dark:text-green-400">Hoch</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Leerstandsquote</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{(100 - insights.occupancyRate).toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnitsManagerModal;

